import { Component, OnInit, OnDestroy, Injector, inject } from '@angular/core';
import { UserSettings } from 'app/core/enums/user-settings.enum';
import { Office } from 'app/core/models/office.model';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { OfficeFilters, OfficeQueueSettings } from 'app/core/models/user-settings/office-search-settings.model';
import { OfficeService, PagedOffices } from 'app/core/services/office.service';
import { UserSettingsService } from 'app/core/services/user-settings.service';
import { UserTimingService } from 'app/core/services/user-timing.service';
import { NewOfficeModalComponent, NewOfficeModalOptions } from 'app/capture-admin/components/new-office-modal/new-office-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { keyedStatusOptions } from 'app/core/options/office-status.opts';
import { StateOptions } from 'app/core/options/states.opts';
import { officeStatusOptions } from 'app/core/options/office-status.opts';
import { NavigationService } from 'app/core/services/navigation.service';
import { debounce } from 'lodash-es';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { DatePipe } from '@angular/common';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { PhoneNumberComponent } from '../../../shared/components/phone-number/phone-number.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BlankValueDashComponent } from '../../../shared/components/blank-value-dash/blank-value-dash.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';

@Component({
    selector: 'app-capture-admin-offices',
    templateUrl: './capture-admin-offices.component.html',
    styleUrls: ['./capture-admin-offices.component.scss'],
    imports: [FormsModule,
              NgSelectComponent,
              MatSlideToggle,
              PaginationHeaderComponent,
              RouterLinkActive,
              RouterLink,
              PhoneNumberComponent,
              FaIconComponent,
              BlankValueDashComponent,
              PaginationComponent,
              MatProgressSpinner,
              DatePipe,
              BlankValueDashPipe]
})
export class CaptureAdminOfficesComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  private userSettingsService = inject(UserSettingsService);
  private officeService = inject(OfficeService);
  private userTimingService = inject(UserTimingService);
  private modalService = inject(NgbModal);
  private navigationService = inject(NavigationService);

  offices: Office[] = [];
  filters: OfficeFilters = new OfficeFilters();
  loading = true;
  paging = LimitOffsetPaging.empty;
  keyedStatusOptions = keyedStatusOptions;
  stateOptions = StateOptions;
  officeStatusOptions = officeStatusOptions;

  debounceFilterChange = debounce(
    () => {
      this.onFilterChange();
    },
    1000,
    { maxWait: 2000 }
  );

  ngOnInit() {
    this.startUserTimingService();
    this.loadQueueSettings();
    this.loadOffices();
    this.openNewOfficeModalForQueryString();
  }

  ngOnDestroy() {
    this.userTimingService.stop();
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadOffices();
    this.saveQueueSettings();
  }

  onPageChange(page: number) {
    this.paging.currentPage = page;
    this.loadOffices();
    this.saveQueueSettings();
  }

  onResetFiltersClick() {
    this.paging.currentPage = 1;
    this.filters = new OfficeFilters();
    this.saveQueueSettings();
    this.loadOffices();
  }

  onAddOfficeClick($event: MouseEvent) {
    $event.preventDefault();

    this.openNewOfficeModal();
  }

  onFilterChange() {
    this.saveQueueSettings();
    this.loadOffices();
  }

  onTextFilterChange() {
    this.debounceFilterChange();
  }

  private openNewOfficeModalForQueryString() {
    const queryParams = this.navigationService.queryParams;

    if (queryParams.new) {
      const options = new NewOfficeModalOptions();
      options.fax = queryParams.new;

      this.openNewOfficeModal(options);
    }
  }

  private startUserTimingService() {
    this.userTimingService.track({
      page: 'capture_admin_offices',
    });
  }

  private loadQueueSettings() {
    const queueSettings: OfficeQueueSettings = this.userSettingsService.get<OfficeQueueSettings>(
      UserSettings.captureAdminOfficeQueueSettings
    );
    if (queueSettings) {
      this.filters = queueSettings.filters;
      this.paging = queueSettings.paging;
    }
  }

  private saveQueueSettings(position = null) {
    const queueSettings = new OfficeQueueSettings();
    queueSettings.position = position;
    queueSettings.filters = this.filters;
    queueSettings.paging = this.paging;
    this.userSettingsService.save<OfficeQueueSettings>(UserSettings.captureAdminOfficeQueueSettings, queueSettings);
  }

  private loadOffices() {
    const paging = { page: this.paging.currentPage, pageSize: this.paging.pageSize };

    this.loading = true;

    this.officeService.getList(paging, this.filters).subscribe((result: PagedOffices) => {
      this.offices = result.records || [];
      this.paging = result.meta.paging;
      this.loading = false;
    });
  }

  private openNewOfficeModal(modalOptions?: NewOfficeModalOptions) {
    const options: any = {
      windowClass: 'edit',
    };

    if (modalOptions) {
      options.injector = Injector.create({
        providers: [{ provide: NewOfficeModalOptions, useValue: modalOptions }],
        parent: this.injector
      })
    }

    this.modalService.open(NewOfficeModalComponent, options);
  }
}
